import { CustomerLead } from "@lib/types/CustomerLead";
import { Customer } from "./Customer";
import { CurrencyEnum } from "./Transaction";
import { SendByteWalletAddressType } from "./SendByte";
import { PaginationType } from ".";

export type AllCurrencies =
  | WalletAddressType
  | SendByteWalletAddressType
  | CurrencyEnum;

export type InvoiceItem = {
  id: string;
  unitPrice?: number;
  discount?: number;
  quantity?: number;
  product?: Product;
};

export type InvoiceBody = {
  id?: string; // edit
  customerId: string;
  products: InvoiceItem[];
  dueDate: string;
  description?: string;
  status?: InvoiceStatus;
  remainingAmount?: number;
  paid?: number;
};

export type Invoice = {
  id: string;

  customerId: string;
  customer?: Customer;
  description: string;
  amount: number;
  paid: number;
  remainingAmount: number;
  currency?: WalletAddressType;
  dueDate: string;
  priority: InvoicePriority;
  status: InvoiceStatus;
  extraData: null | {
    products?: Partial<Product>[];
  };
  orders?: Order[];

  createdAt: string | null;
  updatedAt: string | null;
  deletedAt: string | null;
  createdBy: string | null;
  updatedBy: string | null;
};

export type InvoiceListResponse = PaginationType<Invoice> & {
  totalAmount: number;
  totalPaid: number;
  totalRemainingAmount: number;
  summary: {
    amount: number;
    paid: number;
    remainingAmount: number;
  };
};

export enum InvoiceStatus {
  Paid = "Paid",
  PartiallyPaid = "PartiallyPaid",
  Expired = "Expired",
  Pending = "Pending",
}

export enum InvoicePriority {
  High = "High",
  Medium = "Medium",
  Basic = "Basic",
}

export enum InvoiceProduct {
  CRM = "CRM",
  Meta = "Meta",
  GSuite = "GSuite",
  Whatsapp = "Whatsapp",
}

export type Order = {
  id: string;

  invoiceId: string;
  invoice?: Invoice;

  customerId: string;
  customer?: Customer;

  productId: string;
  product?: Product;

  unitPrice: number;
  netPrice: number;
  discount: number;
  currency: AllCurrencies;
  quantity: number | null;

  createdAt: string | null;
  updatedAt: string | null;
  deletedAt: string | null;
  createdBy: string | null;
  updatedBy: string | null;
};

export type Product = {
  id: string;

  name: string;
  description: string;
  currency: AllCurrencies;
  unitPrice: number;

  createdAt: string | null;
  updatedAt: string | null;
  deletedAt: string | null;
  createdBy: string | null;
  updatedBy: string | null;
};

export enum WalletAddressCreationType {
  SINGLE = "single",
  MULTIPLE = "multiple",
}

export enum WalletAddressType {
  BTC = "BTC",
  ETH = "ETH",
  USDT = "USDT",
}

export enum WalletAddressProtocol {
  Native = "Native",
  ERC20 = "ERC20",
  TRC20 = "TRC20",
  BEP20 = "BEP20",
}

export const WalletAddressProtocolsWithNetwork = {
  [WalletAddressType.BTC]: [WalletAddressProtocol.Native],
  [WalletAddressType.ETH]: [
    WalletAddressProtocol.Native,
    WalletAddressProtocol.ERC20,
  ],
  [WalletAddressType.USDT]: [
    WalletAddressProtocol.TRC20,
    WalletAddressProtocol.ERC20,
  ],
} as const;

export enum WalletType {
  HOT = "hot",
  COLD = "cold",
  // HARDWARE = "hardware",
  // PAPER = "paper",
  // MULTISIG = "multisig",
  // CUSTODIAL = "custodial",
}

export type Wallet = {
  id: string;
  name: string;
  type: WalletType;
  lastCustomId: number | null;

  createdAt: string | null;
  updatedAt: string | null;
  deletedAt: string | null;
  createdBy: string | null;
  updatedBy: string | null;
};

export enum WalletAddressStatus {
  Available = "Available",
  InUse = "InUse",
  Reserved = "Reserved",
  Disabled = "Disabled",
}

export type WalletAddress = {
  id: string;
  address: string;
  balance: string;
  status: WalletAddressStatus;
  walletId: string;
  wallet?: Wallet;
  customerId: string | null;
  customer?: Customer;
  customerUserId: string | null;
  customerUser?: CustomerLead;
  customId: number;
  disabled: boolean;
  lastScanDate: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  deletedAt: string | null;
  createdBy: string | null;
  updatedBy: string | null;
  network: WalletAddressType;
  protocol: WalletAddressProtocol;

  extraData: null | {
    viewUrl?: string;
  };
};

export type CoinProtocolMap = {
  [WalletAddressType.BTC]: WalletAddressProtocol.Native;
  [WalletAddressType.ETH]:
    | WalletAddressProtocol.Native
    | WalletAddressProtocol.ERC20;
  [WalletAddressType.USDT]:
    | WalletAddressProtocol.ERC20
    | WalletAddressProtocol.TRC20
    | WalletAddressProtocol.BEP20;
};

export type WalletAddressRule = WalletAddress &
  (
    | {
        network: WalletAddressType.BTC;
        protocol: WalletAddressProtocol.Native;
      }
    | {
        network: WalletAddressType.ETH;
        protocol: WalletAddressProtocol.Native | WalletAddressProtocol.ERC20;
      }
    | {
        network: WalletAddressType.USDT;
        protocol:
          | WalletAddressProtocol.Native
          | WalletAddressProtocol.ERC20
          | WalletAddressProtocol.TRC20
          | WalletAddressProtocol.BEP20;
      }
  );

export enum HistoryStatus {
  UnmatchedWallet = "UnmatchedWallet",
  InvoiceNotFound = "InvoiceNotFound",
  Success = "Success",
  PendingForScan = "PendingForScan",
  ScanError = "ScanError",
  FundsNotFound = "FundsNotFound",
}

export type BillingHistory = {
  id: string;

  walletAddressId: string | null;
  walletAddress?: WalletAddress;

  customerId: string | null;
  customer?: Customer;

  status: HistoryStatus;
  amount: number;
  network: WalletAddressType;
  error: string | null;

  createdAt: string | null;
  updatedAt: string | null;
  deletedAt: string | null;
  createdBy: string | null;
  updatedBy: string | null;

  extraData: null | {
    address?: string;
    addressLastScan?: string;
    scanned?: {
      lastTxAmount?: number;
      total?: number;
    };
  };
};
